import { type FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useImmer } from 'use-immer';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import { usePipelineNodeRelationships } from '../hooks/usePipelineNodeRelationships';
import type { MeasureJoinTreeNode, DataWhitelist } from '../services/measure.service';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { cn } from '@/lib/utils';
import { Plus, X, ChevronDown, ChevronRight, Check, XCircle, Table2 } from 'lucide-react';

// Internal representation for building custom paths
interface CustomPathNode {
  nodeId: string;
  relationshipId?: string;
  children: CustomPathNode[];
}

interface CustomPathBuilderProps {
  startNodeId: string;
  targetDimensionNodeId: string;
  dataWhitelist: DataWhitelist;
  onSave: (customPath: MeasureJoinTreeNode) => void;
  existingCustomPath?: MeasureJoinTreeNode;
}

interface CustomPathNodeComponentProps {
  node: CustomPathNode;
  upstreamNode?: CustomPathNode;
  path: number[];
  level: number;
  customPath: CustomPathNode;
  onAddNode: (parentPath: number[]) => void;
  onUpdateNode: (path: number[], nodeId: string, relationshipId?: string) => void;
  onRemoveNode: (path: number[]) => void;
  readOnly?: boolean;
  nodesById: Map<string, { id: string; label?: string; name?: string }>;
  relationshipsById: Map<string, { id: string; name: string; parent_node_id: string; child_node_id: string }>;
}

// Helper to get all used relationship IDs from the path tree
function getUsedRelationshipIds(customPath: CustomPathNode): string[] {
  const usedIds: string[] = [];

  const traverse = (nodes: CustomPathNode[]) => {
    nodes.forEach((node) => {
      if (node.relationshipId) {
        usedIds.push(node.relationshipId);
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };

  traverse([customPath]);
  return usedIds;
}

// Recursive component for building path nodes
const CustomPathNodeComponent: FC<CustomPathNodeComponentProps> = ({
  node,
  path,
  level,
  customPath,
  onAddNode,
  onUpdateNode,
  onRemoveNode,
  readOnly,
  upstreamNode,
  nodesById,
  relationshipsById,
}) => {
  const { data: relationships } = usePipelineNodeRelationships();

  const usedRelationshipIds = getUsedRelationshipIds(customPath);

  // Filter relationships to only show those connected to the upstream node
  const availableRelationships = useMemo(() => {
    if (!upstreamNode || !relationships) {
      return [];
    }
    return relationships.filter((rel) => {
      // Allow current selection
      if (rel.id === node.relationshipId) {
        return true;
      }
      // Prevent reuse of relationships
      if (usedRelationshipIds.includes(rel.id)) {
        return false;
      }
      // Only show relationships connected to upstream node
      return rel.child_node_id === upstreamNode.nodeId || rel.parent_node_id === upstreamNode.nodeId;
    });
  }, [relationships, upstreamNode, usedRelationshipIds, node.relationshipId]);

  // Get the node on the other side of the relationship
  const getConnectedNodeId = useCallback(
    (rel: { parent_node_id: string; child_node_id: string }) => {
      if (!upstreamNode) {
        return '';
      }
      return rel.child_node_id === upstreamNode.nodeId ? rel.parent_node_id : rel.child_node_id;
    },
    [upstreamNode]
  );

  const nodeName = nodesById.get(node.nodeId)?.label || nodesById.get(node.nodeId)?.name || node.nodeId;

  return (
    <div key={path.join('-')} style={{ marginLeft: level * 16 }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1">
          {readOnly ? (
            <span className="inline-flex items-center gap-1 font-medium text-sm">
              <Table2 className="h-3 w-3" />
              {nodeName}
            </span>
          ) : (
            <Select
              value={node.relationshipId || ''}
              onValueChange={(value) => {
                const rel = availableRelationships.find((r) => r.id === value);
                if (rel) {
                  onUpdateNode(path, getConnectedNodeId(rel), rel.id);
                }
              }}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select related entity..." />
              </SelectTrigger>
              <SelectContent>
                {availableRelationships.map((rel) => {
                  const targetNodeId = getConnectedNodeId(rel);
                  const targetNode = nodesById.get(targetNodeId);
                  const targetName = targetNode?.label || targetNode?.name || targetNodeId;
                  const relName = relationshipsById.get(rel.id)?.name || 'Unnamed';
                  return (
                    <SelectItem key={rel.id} value={rel.id}>
                      {targetName} (via {relName})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onAddNode(path)}
          disabled={!readOnly && (!node.nodeId || !node.relationshipId)}
          title="Add connection"
        >
          <Plus className="h-4 w-4" />
        </Button>
        {path.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            onClick={() => onRemoveNode(path)}
            title="Remove this connection"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Show children if this node has a relationship selected */}
      {(node.relationshipId || readOnly) && node.children && node.children.length > 0 && (
        <div className="ml-4 border-l pl-2">
          {node.children.map((child, index) => (
            <CustomPathNodeComponent
              key={index}
              node={child}
              path={[...path, index]}
              level={level + 1}
              customPath={customPath}
              onAddNode={onAddNode}
              onUpdateNode={onUpdateNode}
              onRemoveNode={onRemoveNode}
              upstreamNode={node}
              nodesById={nodesById}
              relationshipsById={relationshipsById}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const CustomPathBuilder: FC<CustomPathBuilderProps> = ({
  startNodeId,
  targetDimensionNodeId,
  dataWhitelist,
  onSave,
  existingCustomPath,
}) => {
  const [customPath, setCustomPath] = useImmer<CustomPathNode>({
    nodeId: '',
    relationshipId: '',
    children: [],
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: nodesData } = usePipelineNodes({ page: 1, pageSize: 1000 });
  const { data: relationships } = usePipelineNodeRelationships();

  const nodesById = useMemo(() => {
    if (!nodesData?.records) return new Map();
    return new Map(nodesData.records.map((n) => [n.id, n]));
  }, [nodesData]);

  const relationshipsById = useMemo(() => {
    if (!relationships) return new Map();
    return new Map(relationships.map((r) => [r.id, r]));
  }, [relationships]);

  // Get all required nodes that need to be included in the path
  const getRequiredNodes = useCallback(() => {
    const requiredNodes = [targetDimensionNodeId];

    // Add nodes from data whitelist filters
    if (dataWhitelist.entries) {
      dataWhitelist.entries.forEach((entry) => {
        if (entry.pipeline_node_id && !requiredNodes.includes(entry.pipeline_node_id)) {
          requiredNodes.push(entry.pipeline_node_id);
        }
      });
    }

    return requiredNodes;
  }, [targetDimensionNodeId, dataWhitelist]);

  // Check if a node is included in the custom path
  const isNodeIncluded = useCallback((nodeId: string, nodes: CustomPathNode[]): boolean => {
    for (const node of nodes) {
      if (node.nodeId === nodeId) {
        return true;
      }
      if (node.children && isNodeIncluded(nodeId, node.children)) {
        return true;
      }
    }
    return false;
  }, []);

  // Get validation status for each required node
  const validationStatus = useMemo(() => {
    const requiredNodes = getRequiredNodes();
    return requiredNodes.map((nodeId) => ({
      nodeId,
      isIncluded: isNodeIncluded(nodeId, [customPath]),
      nodeName: nodesById.get(nodeId)?.label || nodesById.get(nodeId)?.name || nodeId,
    }));
  }, [getRequiredNodes, isNodeIncluded, customPath, nodesById]);

  const allRequirementsMet = validationStatus.every((item) => item.isIncluded);

  // Convert existing custom path to our format for pre-population
  const convertJoinTreeToCustomPath = useCallback((tree: MeasureJoinTreeNode): CustomPathNode => {
    if (!tree) {
      return {
        nodeId: '',
        relationshipId: '',
        children: [],
      };
    }

    return {
      nodeId: tree.node_id,
      relationshipId: tree.relationship_id,
      children: tree.downstream ? tree.downstream.map(convertJoinTreeToCustomPath) : [],
    };
  }, []);

  // Convert our custom path back to join tree format
  const convertCustomPathToJoinTree = useCallback((node: CustomPathNode): MeasureJoinTreeNode => {
    return {
      node_id: node.nodeId,
      relationship_id: node.relationshipId,
      downstream: node.children.map(convertCustomPathToJoinTree),
    };
  }, []);

  // Initialize with existing custom path if available
  useEffect(() => {
    if (existingCustomPath) {
      setCustomPath(convertJoinTreeToCustomPath(existingCustomPath));
      setIsExpanded(true);
    } else {
      setCustomPath({ nodeId: startNodeId, children: [] });
    }
  }, [existingCustomPath, startNodeId, convertJoinTreeToCustomPath, setCustomPath]);

  const addNode = useCallback(
    (parentPath: number[]) => {
      setCustomPath((draft) => {
        let current = draft;

        // Navigate to the parent location
        for (let i = 0; i < parentPath.length; i++) {
          current = current.children[parentPath[i]];
        }

        current.children.push({
          nodeId: '',
          children: [],
          relationshipId: '',
        });
      });
    },
    [setCustomPath]
  );

  const updateNode = useCallback(
    (path: number[], nodeId: string, relationshipId?: string) => {
      setCustomPath((draft) => {
        let current = draft;

        for (let i = 0; i < path.length; i++) {
          current = current.children[path[i]];
        }

        current.nodeId = nodeId;
        current.relationshipId = relationshipId;
        // Clear all downstream nodes when upstream selection changes
        current.children = [];
      });
    },
    [setCustomPath]
  );

  const removeNode = useCallback(
    (path: number[]) => {
      setCustomPath((draft) => {
        let current = draft;

        for (let i = 0; i < path.length - 1; i++) {
          current = current.children[path[i]];
        }

        const lastIndex = path[path.length - 1];
        current.children.splice(lastIndex, 1);
      });
    },
    [setCustomPath]
  );

  const handleSave = useCallback(() => {
    const joinTree = convertCustomPathToJoinTree(customPath);
    if (joinTree) {
      onSave(joinTree);
    }
  }, [customPath, convertCustomPathToJoinTree, onSave]);

  const startNodeName = nodesById.get(startNodeId)?.label || nodesById.get(startNodeId)?.name || startNodeId;

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="border rounded-lg p-3">
        <div className="flex justify-between items-center">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 p-0 h-auto hover:bg-transparent">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="font-semibold">Custom Join Path</span>
            </Button>
          </CollapsibleTrigger>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={customPath.children.length === 0 || !customPath.nodeId || !allRequirementsMet}
          >
            Use Custom Path
          </Button>
        </div>

        <CollapsibleContent className="mt-4">
          {/* Validation Status */}
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">
              Your custom path must include all of the following nodes:
            </p>
            <div className="space-y-1">
              {validationStatus.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {item.isIncluded ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                  <span className={cn(item.isIncluded ? 'text-foreground' : 'text-muted-foreground')}>
                    {item.nodeName}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Path Builder */}
          <div className="border rounded p-3 bg-muted/30">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 font-medium text-sm">
                <Table2 className="h-3 w-3" />
                {startNodeName}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => addNode([])}
                title="Add connection"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {customPath.children.length > 0 && (
              <div className="ml-4 border-l pl-2">
                {customPath.children.map((child, index) => (
                  <CustomPathNodeComponent
                    key={index}
                    node={child}
                    path={[index]}
                    level={1}
                    customPath={customPath}
                    onAddNode={addNode}
                    onUpdateNode={updateNode}
                    onRemoveNode={removeNode}
                    upstreamNode={customPath}
                    nodesById={nodesById}
                    relationshipsById={relationshipsById}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
